import React from 'react';

import { AnimatePresence } from 'framer-motion';

import { Sheet } from '@pkg/components';

import { useAuthSidebar } from '@app/store/zustand';

import { AuthFooter } from './AuthFooter';
import { type Web3AuthPanelRenderContext, Web3AuthSteps } from './Web3Auth.types';
import { useWeb3AuthContext, Web3RenderContextProvider } from './context';
import {
    AddTwitterSuccess,
    CombineAccounts,
    EmailActivation,
    EntryPoint,
    ForgotPassword,
    ForgotPasswordConfirmation,
    ResetPassword,
    SignUpWithEmail,
    TwoFactorAuth,
} from './sections';

const componentsByStep = {
    [Web3AuthSteps.AddTwitterSuccess]: AddTwitterSuccess,
    [Web3AuthSteps.CombineAccounts]: CombineAccounts,
    [Web3AuthSteps.EmailActivation]: EmailActivation,
    [Web3AuthSteps.Entry]: EntryPoint,
    [Web3AuthSteps.ForgotPassword]: ForgotPassword,
    [Web3AuthSteps.ForgotPasswordConfirmation]: ForgotPasswordConfirmation,
    [Web3AuthSteps.ResetPassword]: ResetPassword,
    [Web3AuthSteps.SignUpWithEmail]: SignUpWithEmail,
    [Web3AuthSteps.TwoFactorAuth]: TwoFactorAuth,
};

export interface Web3AuthPanelProps {
    renderCtx?: Web3AuthPanelRenderContext;
    step?: string;
    onClose?: () => void;
}

export function Web3AuthPanel({ onClose, renderCtx }: Web3AuthPanelProps) {
    const { currentStep, setCurrentStep } = useWeb3AuthContext();
    const { isAuthSidebarOpen } = useAuthSidebar();

    const CurrentStepComponent = componentsByStep[currentStep];

    const backMarkup =
        currentStep === 'entry' ? null : (
            <Sheet.FixedCloseAction key="BackAction" back onAction={() => setCurrentStep(Web3AuthSteps.Entry)} />
        );

    const closeMarkup = onClose ? <Sheet.FixedCloseAction key="CloseAction" onAction={onClose} /> : undefined;

    const sheetMarkup = isAuthSidebarOpen ? (
        <Sheet key="Web3AuthPanel-Sheet" backdrop onClose={onClose}>
            <AnimatePresence>
                {backMarkup}
                {closeMarkup}
            </AnimatePresence>

            <AnimatePresence mode="wait">
                <Sheet.Main key={`Web3Auth-Step-${currentStep}`}>
                    <Sheet.Layout>
                        <Sheet.Layout.Columns stretch>
                            <Sheet.Layout.Columns.Primary padded scrollable>
                                <CurrentStepComponent />
                            </Sheet.Layout.Columns.Primary>
                        </Sheet.Layout.Columns>
                    </Sheet.Layout>
                </Sheet.Main>
            </AnimatePresence>

            <Sheet.Footer>
                <AuthFooter currentStep={currentStep} />
            </Sheet.Footer>
        </Sheet>
    ) : null;

    return (
        <Web3RenderContextProvider renderCtx={renderCtx}>
            <AnimatePresence>{sheetMarkup}</AnimatePresence>
        </Web3RenderContextProvider>
    );
}
